import { Message, MessageStatus } from '@/types';
import MessageBubble from './MessageBubble';
import { useUIStore } from '@/stores/uiStore';
interface TreeMessageRendererProps {
  treeData: any[];
  versionPath: Record<number, number>;
  conversationId: string;
  depth?: number;
  onSourceClick: (sourceId: string, page?: number) => void;
  onDelete?: (message: Message) => void;
  onRetry?: (message: Message) => void;
  onChangeChoice?: (depth: number, newVersionIndex: number) => void;
  onEditSave?: (
    message: Message,
    newContent: string,
    fileIds?: string[],
    newFiles?: File[]
  ) => void;
  selectedAgent?: {
    id: string;
    name: string;
    logo?: string;
  };
  readOnly?: boolean;
  // 是否有任何消息正在流式传输
  isAnyStreaming?: boolean;
  // 🔥 新增：取消消息的回调（传递消息信息用于调用取消 API）
  onCancel?: (
    messageId: number | string | undefined,
    previousMessageId: number | string | undefined,
    messageStatus: MessageStatus | undefined,
    conversationId: string
  ) => void;
}
/**
 * 递归渲染树形消息结构
 */
export default function TreeMessageRenderer({
  treeData,
  versionPath,
  conversationId,
  depth = 0,
  onSourceClick,
  onDelete,
  onRetry,
  onChangeChoice,
  onEditSave,
  selectedAgent,
  readOnly = false,
  isAnyStreaming = false,
  onCancel,
}: TreeMessageRendererProps) {
  if (!treeData || treeData.length === 0) return null;
  // 获取当前层应该渲染的版本索引
  const selectedVersionIndex = versionPath[depth] ?? 0;
  const selectedNode = treeData[selectedVersionIndex] || treeData[0];
  // 将当前层的所有节点转换为 choices
  const choices = treeData.map((node, idx) => {
    const blocks = convertContentsToBlocks(node.contents, node.id);
    return {
      id: `${node.id}-choice-${idx}`,
      version: idx,
      blocks: blocks,
      tokens: node.tokens ?? 0, // 可选：传递 token 统计（保留 0）
      inputTokens: (node as any).inputTokens ?? 0,
    };
  });
  // 构建 Message 对象
  // 🔥 修复：优先使用 backendId（流式渲染时设置），否则使用 id（API 返回的数字 ID）
  // 流式渲染时 node.id 是 pending 字符串，backendId 是 message_init 返回的数字 ID
  const resolvedBackendId =
    typeof (selectedNode as any).backendId === 'number'
      ? (selectedNode as any).backendId
      : typeof selectedNode.id === 'number'
        ? selectedNode.id
        : undefined;

  const message: Message = {
    id: `${conversationId}-${depth}`,
    backendId: resolvedBackendId,
    previousMessageId: selectedNode.previousMessageId,
    choices: choices,
    activeChoice: selectedVersionIndex, // 添加当前激活的版本索引
    isCanceled: selectedNode.isCanceled || false, // 🔥 新增：是否已被取消
    multiVersion: choices.length, // 添加总版本数
    role: selectedNode.role || 'assistant',
    client: selectedNode.client || undefined,
    timestamp: new Date(selectedNode?.createdAt || Date.now()),
    isStreaming: selectedNode.isStreaming || false, // 从节点读取流式传输状态
    score: selectedNode.score || 0, // 反馈评分
    tokens: selectedNode.tokens ?? choices[selectedVersionIndex]?.tokens ?? 0,
    inputTokens:
      (selectedNode as any).inputTokens ??
      choices[selectedVersionIndex]?.inputTokens ??
      0,
    // 🔥 修复：如果是 user 节点，从 assistant 子节点获取 messageStatus
    // 因为状态更新（waiting→pending→ongoing）是更新 assistant 节点的状态
    messageStatus: (() => {
      const nodeStatus = (selectedNode as any).messageStatus;
      if (nodeStatus) return nodeStatus;
      // 如果当前节点没有 messageStatus，尝试从 assistant 子节点获取
      if (selectedNode.role === 'user' && selectedNode.children?.length > 0) {
        const assistantChild = selectedNode.children.find(
          (child: any) => child.role === 'assistant'
        );
        if (assistantChild?.messageStatus) {
          return assistantChild.messageStatus;
        }
      }
      return undefined;
    })(),
  };
  // 处理版本切换
  const handleChangeChoice = (_messageId: string, newActiveChoice: number) => {
    if (onChangeChoice) {
      // 版本切换器控制的是当前层的版本选择
      onChangeChoice(depth, newActiveChoice);
    }
  };

  // 🔥 新增：过滤掉以下类型的节点：
  // 1. 只包含 hash-code 类型内容的 assistant 节点
  // 2. contents 为空或不存在的节点（占位符节点，用于保持数据结构）
  // 3. type 为 'cancel' 的节点（取消的消息不渲染，但保留在树中用于 previousMessageId 查找）
  // 🔥 修改：流式传输中的节点不跳过，以显示 loading 圆点
  const shouldSkipNode =
    // 🔥 新增：跳过 cancel 类型节点（不渲染，但保留在树中用于 previousMessageId 查找）
    (selectedNode as any).type === 'cancel' ||
    (selectedNode.role === 'assistant' &&
      !selectedNode.isStreaming && // 🔥 流式传输中的节点不跳过
      // 情况1：空 contents（占位符节点）
      (!selectedNode.contents ||
        selectedNode.contents.length === 0 ||
        // 情况2：只包含 hash-code 类型
        (selectedNode.contents?.length === 1 &&
          selectedNode.contents[0]?.type === 'hash-code')));

  if (shouldSkipNode) {
    // 跳过该节点，但继续渲染子节点
    return selectedNode.children && selectedNode.children.length > 0 ? (
      <TreeMessageRenderer
        treeData={selectedNode.children}
        versionPath={versionPath}
        conversationId={conversationId}
        depth={depth + 1}
        onSourceClick={onSourceClick}
        onDelete={onDelete}
        onRetry={onRetry}
        onChangeChoice={onChangeChoice}
        onEditSave={onEditSave}
        selectedAgent={selectedAgent}
        readOnly={readOnly}
        isAnyStreaming={isAnyStreaming}
        onCancel={onCancel}
      />
    ) : null;
  }

  return (
    <>
      <MessageBubble
        message={message}
        onSourceClick={onSourceClick}
        onDelete={onDelete ? () => onDelete(message) : undefined}
        onRetry={onRetry ? () => onRetry(message) : undefined}
        readOnly={readOnly}
        onChangeChoice={handleChangeChoice}
        onEditSave={onEditSave}
        selectedAgent={selectedAgent}
        conversationId={conversationId}
        isAnyStreaming={isAnyStreaming}
        onCancel={(() => {
          if (!onCancel) return undefined;

          const status = message.messageStatus;

          // 🔥 修复问题3：如果消息已被取消，不显示 Cancel 按钮（显示 canceled 状态）
          if (message.isCanceled) {
            return undefined;
          }

          // 🔥 业务逻辑修改：只有 waiting 状态支持 Cancel
          // pending 和 ongoing 状态不支持 Cancel（因为请求已发送到后端）
          if (status !== 'waiting') {
            return undefined;
          }

          return () => {
            // 🔥 userMessageId = 当前要取消的消息的 ID（当前节点的 backendId）
            const userMessageId = message.backendId ?? selectedNode.id;

            // 🔥 点击时重新获取 latestInit（获取最新的 assistantMessageId）
            const latestInitNow = useUIStore
              .getState()
              .getLatestMessageInit(conversationId);

            console.log('🛑 Cancel 按钮点击 - 检查 latestMessageInit', {
              conversationId,
              userMessageId,
              latestInitNow,
              latestMessageInitMap: useUIStore.getState().latestMessageInitMap,
            });

            onCancel(
              userMessageId,
              latestInitNow?.assistantMessageId, // 🔥 应该使用最新的 assistantMessageId
              status,
              conversationId
            );
          };
        })()}
      />
      {/* 递归渲染子节点 */}
      {selectedNode.children && selectedNode.children.length > 0 && (
        <TreeMessageRenderer
          treeData={selectedNode.children}
          versionPath={versionPath}
          conversationId={conversationId}
          depth={depth + 1}
          onSourceClick={onSourceClick}
          onDelete={onDelete}
          onRetry={onRetry}
          onChangeChoice={onChangeChoice}
          onEditSave={onEditSave}
          selectedAgent={selectedAgent}
          readOnly={readOnly}
          isAnyStreaming={isAnyStreaming}
          onCancel={onCancel}
        />
      )}
    </>
  );
}
/**
 * 将API的contents数组转换为MessageBlock数组
 */
function convertContentsToBlocks(contents: any[], nodeId: number): any[] {
  if (!contents || contents.length === 0) return [];
  return contents.map((item: any, idx: number) => {
    let itemContent = item.content;
    if (item.type === 'ref') {
      const url = `${window.location.origin}${encodeURIComponent(`/api/files/retrieve?filePath=${item.file}&folderID=${item.folderID}`)}${item.page ? `#page=${item.page}` : ''}`;
      const htmlLink = `[${item.order}] <a href="#" data-pdf-url="${url}" data-pdf-filename="${item.file}" style="color: #0066cc; text-decoration: underline; cursor: pointer;">${extractFileName(item.file)}</a>`;
      itemContent = htmlLink;
    }
    return {
      type: item.type || 'text',
      fileId: item.id || null,
      id: `${nodeId}-${idx}`,
      content: String(itemContent),
      collapsed: item.type === 'think' ? true : false,
    };
  });
}
function extractFileName(filePath: string): string {
  if (!filePath) return 'Unknown File';
  const parts = filePath.split('/');
  return parts[parts.length - 1] || 'Unknown File';
}
